import { useEffect, useState, useRef, useCallback } from 'react';
import { Mic, Volume2, ArrowRight, Heart, Loader } from 'lucide-react';

const MindfulnessMatcher = () => {
  const [sessionState, setSessionState] = useState('idle');
  const [statusMessage, setStatusMessage] = useState('');
  const [matchResult, setMatchResult] = useState(null);
  const [qaHistory, setQaHistory] = useState([]);
  const [inferredQAs, setInferredQAs] = useState({});
  const [inferredQuestions, setInferredQuestions] = useState([]);
  const [currentQuestionReason, setCurrentQuestionReason] = useState('');
  const [userAudioLevels, setUserAudioLevels] = useState(new Array(32).fill(0));
  const [aiAudioLevels, setAiAudioLevels] = useState(new Array(32).fill(0));
  
  const userAnalyzerRef = useRef(null);
  const aiAnalyzerRef = useRef(null);
  const animationFrameRef = useRef(null);
  const isSessionStoppingRef = useRef(false);
  const preInitializedStreamRef = useRef(null);
  const preInitializedAudioContextRef = useRef(null);

  let globalSocket = null;
  let mediaRecorder = null;
  let audioStream = null;
  let currentAudioContext = null;

  useEffect(() => {
    return () => {
      cleanupMicrophone();
      if (window.GLOBAL_WEBSOCKET) {
        window.GLOBAL_WEBSOCKET.close();
        window.GLOBAL_WEBSOCKET = null;
      }
    };
  }, []);

  const cleanupMicrophone = useCallback(() => {
    if (preInitializedStreamRef.current) {
      preInitializedStreamRef.current.getTracks().forEach(track => {
        track.stop();
        track.enabled = false;
      });
      preInitializedStreamRef.current = null;
    }
    
    if (audioStream) {
      audioStream.getTracks().forEach(track => {
        track.stop();
        track.enabled = false;
      });
      audioStream = null;
    }
    
    if (preInitializedAudioContextRef.current?.state !== 'closed') {
      preInitializedAudioContextRef.current?.close();
      preInitializedAudioContextRef.current = null;
    }
    
    if (currentAudioContext?.state !== 'closed') {
      currentAudioContext?.close();
      currentAudioContext = null;
    }
    
    if (mediaRecorder?.state === 'recording') {
      mediaRecorder.stop();
      mediaRecorder = null;
    }
  }, []);

  const getWebSocket = () => {
    if (!window.GLOBAL_WEBSOCKET || window.GLOBAL_WEBSOCKET.readyState !== WebSocket.OPEN) {
      window.GLOBAL_WEBSOCKET = new WebSocket('wss://mindaera.com/ws');
      
      window.GLOBAL_WEBSOCKET.onerror = (err) => {
        console.error('WebSocket error:', err);
      };
      
      window.GLOBAL_WEBSOCKET.onclose = () => {
        console.log('WebSocket closed');
        window.GLOBAL_WEBSOCKET = null;
      };
    }
    return window.GLOBAL_WEBSOCKET;
  };

  const CircularVisualizer = ({ userLevels, aiLevels, isUserActive, isAiActive }) => {
    const centerSize = 100;
    const ringWidth = 5;
    const bars = 32;
    
    const createCircularBars = (levels, radius, isActive, color) => {
      const angleStep = (2 * Math.PI) / bars;
      
      return levels.map((level, i) => {
        const angle = i * angleStep - Math.PI / 2;
        const barHeight = isActive ? Math.max(3, level * 30) : 3;
        const innerRadius = radius;
        const outerRadius = radius + barHeight;
        
        const x1 = Math.cos(angle) * innerRadius + 150;
        const y1 = Math.sin(angle) * innerRadius + 150;
        const x2 = Math.cos(angle) * outerRadius + 150;
        const y2 = Math.sin(angle) * outerRadius + 150;
        
        const opacity = isActive ? Math.max(0.4, level * 1.2) : 0.2;
        
        return (
          <line
            key={i}
            x1={x1} y1={y1} x2={x2} y2={y2}
            stroke={color}
            strokeWidth={ringWidth}
            strokeLinecap="round"
            opacity={opacity}
            className="transition-all duration-150"
          />
        );
      });
    };

    return (
      <div className="flex items-center justify-center mb-8">
        <svg width="300" height="300">
          <defs>
            <radialGradient id="centerGrad" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#94a3b8" stopOpacity="0.2" />
              <stop offset="100%" stopColor="#94a3b8" stopOpacity="0.05" />
            </radialGradient>
          </defs>
          
          <circle cx="150" cy="150" r={centerSize / 2} fill="url(#centerGrad)" />
          {createCircularBars(aiLevels, centerSize / 2 + 15, isAiActive, '#6366f1')}
          {createCircularBars(userLevels, centerSize / 2 + 60, isUserActive, '#8b5cf6')}
          
          <foreignObject x="125" y="125" width="50" height="50">
            <div className="w-full h-full flex items-center justify-center">
              {isAiActive && <Volume2 size={24} className="text-indigo-500" />}
              {isUserActive && <Mic size={24} className="text-purple-500" />}
            </div>
          </foreignObject>
        </svg>
      </div>
    );
  };

  const analyzeAudio = (analyser, setLevels) => {
    if (!analyser) return;
    
    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    
    const animate = () => {
      analyser.getByteFrequencyData(dataArray);
      
      const speechStartBin = Math.floor((80 / 12000) * bufferLength);
      const speechEndBin = Math.floor((3400 / 12000) * bufferLength);
      
      let totalEnergy = 0;
      let count = 0;
      
      for (let i = speechStartBin; i < speechEndBin && i < bufferLength; i++) {
        totalEnergy += dataArray[i];
        count++;
      }
      
      const averageEnergy = count > 0 ? (totalEnergy / count) / 255 : 0;
      const baseLevel = Math.pow(averageEnergy, 0.6);
      
      const bars = 32;
      const newLevels = [];
      
      for (let i = 0; i < bars; i++) {
        const angle = (i / bars) * 2 * Math.PI;
        const wave1 = Math.sin(angle * 2 + Date.now() * 0.003) * 0.3;
        const wave2 = Math.sin(angle * 3 + Date.now() * 0.005) * 0.2;
        const wave3 = Math.sin(angle * 5 + Date.now() * 0.007) * 0.1;
        
        const variation = (wave1 + wave2 + wave3) * baseLevel;
        const level = Math.max(0, Math.min(1, baseLevel + variation + (Math.random() * 0.1 * baseLevel)));
        
        newLevels.push(level);
      }
      
      setLevels(newLevels);
      
      if (animationFrameRef.current) {
        animationFrameRef.current = requestAnimationFrame(animate);
      }
    };
    
    animationFrameRef.current = requestAnimationFrame(animate);
  };

  const speakWithVoiceAPI = async (text, tone = 'calm') => {
    if (isSessionStoppingRef.current) return Promise.resolve();
    
    setSessionState('ai-speaking');
    setStatusMessage("Amy is speaking...");
    
    return new Promise(async (resolve, reject) => {
      try {
        const socket = getWebSocket();
        
        if (socket.readyState === WebSocket.CONNECTING) {
          await new Promise((r) => { socket.onopen = r; });
        }

        let audioContext = new (window.AudioContext || window.webkitAudioContext)();
        if (audioContext.state === 'suspended') await audioContext.resume();
        
        let analyser = audioContext.createAnalyser();
        analyser.fftSize = 512;
        analyser.smoothingTimeConstant = 0.8;
        aiAnalyzerRef.current = analyser;
        analyzeAudio(analyser, setAiAudioLevels);

        let mediaSource = new MediaSource();
        const url = URL.createObjectURL(mediaSource);
        let audio = new Audio(url);
        audio.preload = 'auto';

        const source = audioContext.createMediaElementSource(audio);
        source.connect(analyser);
        source.connect(audioContext.destination);

        let sourceBuffer = null;
        let isSourceBufferReady = false;
        let pendingChunks = [];
        let isStreamEnded = false;

        await new Promise((resolveSourceOpen) => {
          mediaSource.addEventListener('sourceopen', () => {
            try {
              mediaSource.duration = 0;
              sourceBuffer = mediaSource.addSourceBuffer('audio/mpeg');
              sourceBuffer.mode = 'sequence';
              isSourceBufferReady = true;

              sourceBuffer.addEventListener('updateend', () => {
                if (pendingChunks.length > 0 && !sourceBuffer.updating) {
                  processChunks();
                } else if (isStreamEnded && pendingChunks.length === 0) {
                  finalizeStream();
                }
              });

              audio.addEventListener('canplay', () => {
                if (audio.paused) audio.play().catch(console.error);
              }, { once: true });
              
              resolveSourceOpen();
            } catch (err) {
              console.error('SourceBuffer setup error:', err);
              resolveSourceOpen();
            }
          });
        });

        const processChunks = () => {
          if (!isSourceBufferReady || !sourceBuffer || sourceBuffer.updating || pendingChunks.length === 0) return;

          const MAX_SIZE = 128 * 1024;
          let bytesToAppend = 0;
          let chunksToProcess = [];
          
          while (pendingChunks.length > 0 && bytesToAppend < MAX_SIZE) {
            const nextChunk = pendingChunks[0];
            if (bytesToAppend + nextChunk.length <= MAX_SIZE || chunksToProcess.length === 0) {
              chunksToProcess.push(pendingChunks.shift());
              bytesToAppend += nextChunk.length;
            } else {
              break;
            }
          }
          
          if (chunksToProcess.length === 0) return;
          
          try {
            const combined = new Uint8Array(bytesToAppend);
            let offset = 0;
            for (const chunk of chunksToProcess) {
              combined.set(chunk, offset);
              offset += chunk.length;
            }
            sourceBuffer.appendBuffer(combined);
          } catch (err) {
            console.warn('Buffer append failed:', err);
            pendingChunks.unshift(...chunksToProcess);
          }
        };

        const finalizeStream = () => {
          if (mediaSource?.readyState === 'open' && sourceBuffer && !sourceBuffer.updating) {
            try {
              const buffered = sourceBuffer.buffered;
              if (buffered.length > 0) {
                const duration = buffered.end(buffered.length - 1);
                if (mediaSource.readyState === 'open') mediaSource.duration = duration;
              }
              if (mediaSource.readyState === 'open') mediaSource.endOfStream();
            } catch (err) {
              console.warn('Stream finalization error:', err);
            }
          }
        };

        const messageHandler = (event) => {
          if (isSessionStoppingRef.current) {
            socket.removeEventListener('message', messageHandler);
            cleanup();
            resolve();
            return;
          }

          try {
            const message = JSON.parse(event.data);

            if (message.type === 'tts-audio-chunk') {
              const binary = Uint8Array.from(atob(message.data), c => c.charCodeAt(0));
              pendingChunks.push(binary);
              if (isSourceBufferReady && sourceBuffer && !sourceBuffer.updating) processChunks();
            } else if (message.type === 'tts-audio-complete') {
              isStreamEnded = true;
              if (isSourceBufferReady && sourceBuffer && !sourceBuffer.updating) processChunks();
            } else if (message.error) {
              cleanup();
              reject(new Error(message.error));
            }
          } catch (err) {
            console.error('TTS message parse error:', err);
          }
        };

        const cleanup = () => {
          socket.removeEventListener('message', messageHandler);
          if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
            animationFrameRef.current = null;
          }
          setAiAudioLevels(new Array(32).fill(0));
          aiAnalyzerRef.current = null;
        };

        audio.onended = () => {
          cleanup();
          resolve();
        };

        audio.onerror = (err) => {
          cleanup();
          reject(new Error('Audio playback failed'));
        };

        socket.addEventListener('message', messageHandler);
        socket.send(JSON.stringify({ type: 'tts', text, voice: 'alloy', tone }));

      } catch (err) {
        console.error('Speak error:', err);
        reject(err);
      }
    });
  };

  const listenWithVoiceAPI = async () => {
    if (isSessionStoppingRef.current) return { transcript: '', tone: 'neutral' };

    setSessionState('listening');
    setStatusMessage('Go ahead, I\'m listening...');

    return new Promise(async (resolve, reject) => {
      try {
        const socket = getWebSocket();
        if (socket.readyState === WebSocket.CONNECTING) {
          await new Promise(r => { socket.onopen = r; });
        }

        let audioContext, mediaStreamSource, stream;
        let isProcessing = true;
        let recorder = null;
        let hasSpeech = false;
        let silenceTimer = null;
        let analysisResult = null;
        let waitingForAudio = false;
        
        // TTS playback variables
        let ttsAudioContext = null;
        let ttsAnalyser = null;
        let ttsMediaSource = null;
        let ttsSourceBuffer = null;
        let ttsAudio = null;
        let ttsPendingChunks = [];
        let ttsIsSourceBufferReady = false;
        let ttsIsStreamEnded = false;

        const SILENCE_THRESHOLD = 0.02;
        const SILENCE_DURATION = 1500;

        const messageHandler = async (event) => {
          console.log('📩 Received message:', event.data);
          const data = JSON.parse(event.data);
          console.log('📦 Parsed data:', data);
          
          if (isSessionStoppingRef.current) {
            console.log('🛑 Session stopping, cleaning up');
            cleanupAudio();
            resolve({ transcript: '', tone: 'neutral' });
            return;
          }

          if (data.type === 'listening-ready') {
            console.log('🎤 Listening ready, starting microphone');
            try {
              stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                  sampleRate: { ideal: 24000 },
                  channelCount: { exact: 1 },
                  echoCancellation: true,
                  noiseSuppression: true,
                  autoGainControl: true
                }
              });

              audioContext = new AudioContext({ sampleRate: 24000 });
              currentAudioContext = audioContext;
              if (audioContext.state === 'suspended') await audioContext.resume();

              const analyser = audioContext.createAnalyser();
              analyser.fftSize = 512;
              analyser.smoothingTimeConstant = 0.8;
              userAnalyzerRef.current = analyser;

              mediaStreamSource = audioContext.createMediaStreamSource(stream);
              mediaStreamSource.connect(analyser);

              recorder = new MediaRecorder(stream, {
                mimeType: 'audio/webm;codecs=opus',
                audioBitsPerSecond: 32000
              });

              recorder.ondataavailable = (event) => {
                if (event.data.size > 0 && isProcessing) {
                  console.log('🎙️ Audio chunk available, size:', event.data.size);
                  const reader = new FileReader();
                  reader.onload = () => {
                    const base64 = reader.result.split(',')[1];
                    if (socket.readyState === WebSocket.OPEN) {
                      console.log('📤 Sending audio chunk to server');
                      socket.send(JSON.stringify({ type: 'audio-chunk', audio: base64 }));
                    }
                  };
                  reader.readAsDataURL(event.data);
                }
              };

              const analyzeVAD = () => {
                if (!isProcessing || !analyser) return;

                const bufferLength = analyser.frequencyBinCount;
                const timeData = new Uint8Array(bufferLength);
                const freqData = new Uint8Array(bufferLength);
                
                analyser.getByteTimeDomainData(timeData);
                analyser.getByteFrequencyData(freqData);
                
                let sum = 0;
                for (let i = 0; i < timeData.length; i++) {
                  const sample = (timeData[i] - 128) / 128;
                  sum += sample * sample;
                }
                const rms = Math.sqrt(sum / timeData.length);
                
                const isSpeech = rms > SILENCE_THRESHOLD;
                
                if (isSpeech) {
                  if (!hasSpeech) {
                    console.log('🗣️ Speech detected! RMS:', rms);
                    hasSpeech = true;
                    setStatusMessage('Listening to you...');
                  }
                  if (silenceTimer) {
                    clearTimeout(silenceTimer);
                    silenceTimer = null;
                  }
                } else {
                  if (!silenceTimer && hasSpeech) {
                    console.log('🤫 Silence detected, starting timer');
                    silenceTimer = setTimeout(() => {
                      console.log('⏰ Silence timeout reached, stopping recording');
                      isProcessing = false;
                      setSessionState('processing');
                      setStatusMessage('Processing...');
                      
                      if (recorder.state === 'recording') {
                        console.log('🛑 Stopping recorder');
                        recorder.stop();
                      }
                      setTimeout(() => {
                        if (socket.readyState === WebSocket.OPEN) {
                          console.log('📤 Sending stop-listening signal');
                          socket.send(JSON.stringify({ type: 'stop-listening' }));
                        }
                      }, 100);
                    }, SILENCE_DURATION);
                  }
                }
                
                if (isProcessing) requestAnimationFrame(analyzeVAD);
              };

              recorder.start(100);
              analyzeVAD();
            } catch (err) {
              console.error('Microphone error:', err);
              reject(err);
            }
          } else if (data.type === 'analysis-complete') {
            console.log('✅ Analysis complete received:', data);
            analysisResult = {
              transcript: data.transcript.text,
              tone: data.emotion,
              therapeuticResponse: data.therapeuticResponse,
              inference: data.inference
            };
            console.log('📝 Analysis result:', analysisResult);
            
            if (!waitingForAudio) {
              console.log('🎯 Not waiting for audio, resolving immediately');
              cleanupAudio();
              socket.removeEventListener('message', messageHandler);
              resolve(analysisResult);
            } else {
              console.log('⏳ Waiting for audio to complete...');
            }
          } else if (data.type === 'tts-audio-start') {
            console.log('🔊 TTS audio starting');
            waitingForAudio = true;
            setSessionState('ai-speaking');
            setStatusMessage('Amy is responding...');
            
            // Initialize TTS audio playback
            try {
              ttsAudioContext = new (window.AudioContext || window.webkitAudioContext)();
              if (ttsAudioContext.state === 'suspended') await ttsAudioContext.resume();
              
              ttsAnalyser = ttsAudioContext.createAnalyser();
              ttsAnalyser.fftSize = 512;
              ttsAnalyser.smoothingTimeConstant = 0.8;
              aiAnalyzerRef.current = ttsAnalyser;
              analyzeAudio(ttsAnalyser, setAiAudioLevels);

              ttsMediaSource = new MediaSource();
              const url = URL.createObjectURL(ttsMediaSource);
              ttsAudio = new Audio(url);
              ttsAudio.preload = 'auto';

              const source = ttsAudioContext.createMediaElementSource(ttsAudio);
              source.connect(ttsAnalyser);
              source.connect(ttsAudioContext.destination);

              await new Promise((resolveSourceOpen) => {
                ttsMediaSource.addEventListener('sourceopen', () => {
                  console.log('🎵 TTS MediaSource opened');
                  try {
                    ttsMediaSource.duration = 0;
                    ttsSourceBuffer = ttsMediaSource.addSourceBuffer('audio/mpeg');
                    ttsSourceBuffer.mode = 'sequence';
                    ttsIsSourceBufferReady = true;

                    ttsSourceBuffer.addEventListener('updateend', () => {
                      if (ttsPendingChunks.length > 0 && !ttsSourceBuffer.updating) {
                        processTTSChunks();
                      } else if (ttsIsStreamEnded && ttsPendingChunks.length === 0) {
                        finalizeTTSStream();
                      }
                    });

                    ttsAudio.addEventListener('canplay', () => {
                      if (ttsAudio.paused) {
                        console.log('▶️ Starting TTS playback');
                        ttsAudio.play().catch(console.error);
                      }
                    }, { once: true });
                    
                    resolveSourceOpen();
                  } catch (err) {
                    console.error('TTS SourceBuffer setup error:', err);
                    resolveSourceOpen();
                  }
                });
              });
              
              // Set up audio ended handler
              ttsAudio.onended = () => {
                console.log('✅ TTS playback ended');
                cleanupTTSAudio();
                
                // Check if we already have analysis result
                if (analysisResult) {
                  console.log('🎯 Analysis already complete, resolving');
                  cleanupAudio();
                  socket.removeEventListener('message', messageHandler);
                  resolve(analysisResult);
                }
              };
              
            } catch (err) {
              console.error('Failed to initialize TTS audio:', err);
            }
          } else if (data.type === 'tts-audio-chunk') {
            console.log('🎵 TTS audio chunk received, size:', data.data.length);
            const binary = Uint8Array.from(atob(data.data), c => c.charCodeAt(0));
            ttsPendingChunks.push(binary);
            
            if (ttsIsSourceBufferReady && ttsSourceBuffer && !ttsSourceBuffer.updating) {
              processTTSChunks();
            }
          } else if (data.type === 'tts-audio-complete') {
            console.log('✅ TTS streaming complete');
            ttsIsStreamEnded = true;
            waitingForAudio = false;
            
            if (ttsIsSourceBufferReady && ttsSourceBuffer && !ttsSourceBuffer.updating) {
              processTTSChunks();
            }
            
            // If we already have analysis, resolve now
            if (analysisResult && !ttsAudio) {
              console.log('🎯 Analysis done and no audio, resolving');
              cleanupAudio();
              socket.removeEventListener('message', messageHandler);
              resolve(analysisResult);
            }
          } else if (data.type === 'session-ready') {
            console.log('✅ Session ready');
          } else {
            console.log('❓ Unknown message type:', data.type);
          }
        };

        const processTTSChunks = () => {
          if (!ttsIsSourceBufferReady || !ttsSourceBuffer || ttsSourceBuffer.updating || ttsPendingChunks.length === 0) {
            return;
          }

          const MAX_SIZE = 128 * 1024;
          let bytesToAppend = 0;
          let chunksToProcess = [];
          
          while (ttsPendingChunks.length > 0 && bytesToAppend < MAX_SIZE) {
            const nextChunk = ttsPendingChunks[0];
            if (bytesToAppend + nextChunk.length <= MAX_SIZE || chunksToProcess.length === 0) {
              chunksToProcess.push(ttsPendingChunks.shift());
              bytesToAppend += nextChunk.length;
            } else {
              break;
            }
          }
          
          if (chunksToProcess.length === 0) return;
          
          try {
            const combined = new Uint8Array(bytesToAppend);
            let offset = 0;
            for (const chunk of chunksToProcess) {
              combined.set(chunk, offset);
              offset += chunk.length;
            }
            ttsSourceBuffer.appendBuffer(combined);
            console.log('📦 Appended TTS chunk:', bytesToAppend, 'bytes');
          } catch (err) {
            console.warn('TTS buffer append failed:', err);
            ttsPendingChunks.unshift(...chunksToProcess);
          }
        };

        const finalizeTTSStream = () => {
          if (ttsMediaSource?.readyState === 'open' && ttsSourceBuffer && !ttsSourceBuffer.updating) {
            try {
              const buffered = ttsSourceBuffer.buffered;
              if (buffered.length > 0) {
                const duration = buffered.end(buffered.length - 1);
                if (ttsMediaSource.readyState === 'open') ttsMediaSource.duration = duration;
              }
              if (ttsMediaSource.readyState === 'open') {
                console.log('🏁 Ending TTS MediaSource stream');
                ttsMediaSource.endOfStream();
              }
            } catch (err) {
              console.warn('TTS stream finalization error:', err);
            }
          }
        };

        const cleanupTTSAudio = () => {
          if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
            animationFrameRef.current = null;
          }
          setAiAudioLevels(new Array(32).fill(0));
          aiAnalyzerRef.current = null;
          
          if (ttsAudioContext?.state !== 'closed') {
            ttsAudioContext?.close().catch(console.warn);
          }
        };

        const cleanupAudio = () => {
          isProcessing = false;
          if (silenceTimer) clearTimeout(silenceTimer);
          if (recorder?.state === 'recording') recorder.stop();
          if (mediaStreamSource) mediaStreamSource.disconnect();
          if (audioContext?.state !== 'closed') audioContext.close();
          if (stream) stream.getTracks().forEach(t => t.stop());
          setUserAudioLevels(new Array(32).fill(0));
          userAnalyzerRef.current = null;
          
          // Also cleanup TTS
          cleanupTTSAudio();
        };

        socket.addEventListener('message', messageHandler);
        
        console.log('📤 Sending init-session');
        socket.send(JSON.stringify({
          type: 'init-session',
          previousQA: qaHistory,
          inferredQuestionIds: inferredQuestions
        }));

        console.log('⏳ Waiting for session-ready...');
        await new Promise((resolveSession) => {
          const sessionHandler = (event) => {
            const data = JSON.parse(event.data);
            console.log('📩 Session init message:', data);
            if (data.type === 'session-ready') {
              console.log('✅ Session ready confirmed');
              resolveSession();
            }
          };
          socket.addEventListener('message', sessionHandler);
          setTimeout(() => {
            console.log('⏰ Session ready timeout');
            resolveSession();
          }, 5000);
        });

        console.log('📤 Sending start-listening');
        socket.send(JSON.stringify({ type: 'start-listening' }));

      } catch (err) {
        console.error('Listen error:', err);
        reject(err);
      }
    });
  };

  const loopVoiceSession = async (previousQuestionId = null, previousQuestion = null, currentHistory = qaHistory) => {
    if (isSessionStoppingRef.current) {
      console.log('🛑 Loop stopped - session stopping');
      return;
    }

    console.log('🔄 Voice loop iteration', { previousQuestionId, previousQuestion });

    let result;
    try {
      console.log('👂 Starting listen...');
      result = await listenWithVoiceAPI();
      console.log('✅ Listen complete, result:', result);
    } catch (err) {
      console.error('❌ Listen failed:', err);
      setSessionState('idle');
      return;
    }
    
    if (isSessionStoppingRef.current) {
      console.log('🛑 Session stopped after listen');
      return;
    }

    let updatedHistory = currentHistory;
    if (previousQuestionId && previousQuestion && result.transcript) {
      console.log('📝 Updating Q&A history');
      updatedHistory = [...currentHistory, {
        questionId: previousQuestionId,
        question: previousQuestion,
        answer: result.transcript
      }];
      setQaHistory(updatedHistory);
    }

    if (result.sessionComplete) {
      console.log('🏁 Session complete');
      setSessionState('complete');
      return;
    }

    if (result.inference?.inferredAnswers?.length > 0) {
      console.log('🧠 Updating inferred answers:', result.inference.inferredAnswers);
      setInferredQuestions(prev => [...new Set([...prev, ...result.inference.inferredAnswers])]);
      
      const newInferredQAs = {};
      result.inference.inferredAnswers.forEach(qId => {
        const reasoning = result.inference.inferenceMapping?.[qId] || `Inferred from: "${result.transcript.substring(0, 100)}..."`;
        newInferredQAs[qId] = {
          answer: `[Inferred] ${reasoning}`,
          fullTranscript: result.transcript,
          reasoning
        };
      });
      
      setInferredQAs(prev => ({...prev, ...newInferredQAs}));
    }

    const nextQuestionId = result.therapeuticResponse?.questionId || null;
    const nextQuestion = result.therapeuticResponse?.spokenText || '';
    
    console.log('❓ Next question:', { nextQuestionId, nextQuestion });
    
    if (result.therapeuticResponse?.reasonForQuestion) {
      setCurrentQuestionReason(result.therapeuticResponse.reasonForQuestion);
    }

    if (nextQuestion && !isSessionStoppingRef.current) {
      console.log('➡️ Continuing loop with next question');
      await loopVoiceSession(nextQuestionId, nextQuestion, updatedHistory);
    } else {
      console.log('⚠️ No next question, ending loop');
      setSessionState('idle');
    }
  };

  const startSession = async () => {
    setSessionState('ai-speaking');
    setStatusMessage('Preparing...');
    
    try {
      const socket = getWebSocket();
      if (socket.readyState === WebSocket.CONNECTING) {
        await new Promise((resolve) => { socket.onopen = resolve; });
      }
      
      const initialPrompt = "Hi, I'm Amy. I'm here to help find the perfect therapist for you. What's on your mind?";
      await speakWithVoiceAPI(initialPrompt, 'warm');
      await loopVoiceSession();
    } catch (error) {
      console.error('Session start failed:', error);
      setSessionState('idle');
      setStatusMessage('Failed to start. Please try again.');
    }
  };

  const generateMatch = async () => {
    isSessionStoppingRef.current = true;
    cleanupMicrophone();
    
    setSessionState('analyzing');
    setStatusMessage('Finding your perfect match...');

    try {
      const response = await fetch('/api/natural-matchmaker/finalize', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          previousQA: qaHistory,
          inferredQAs: inferredQAs
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setMatchResult(data.recommendation);
        await speakWithVoiceAPI(
          `Based on everything you've shared, I've found the perfect therapist for you. ${data.recommendation.name} specializes in ${data.recommendation.specialty}. ${data.recommendation.matchReasoning}`, 
          'enthusiastic'
        );
      }

      setSessionState('complete');
    } catch (error) {
      console.error('Match generation failed:', error);
      setStatusMessage('Failed to generate match. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <div className="flex-grow flex items-center justify-center p-6">
        {sessionState === 'idle' && (
          <div className="text-center max-w-2xl">
            <h1 className="text-5xl font-light text-gray-800 mb-8 tracking-wider">
              MINDFULNESS
            </h1>
            
            <div className="space-y-4 mb-12 text-gray-700">
              <p className="text-lg">Every person is different.</p>
              <p className="text-lg">Every therapist is different.</p>
              <p className="text-lg font-medium">The match between you,</p>
              <p className="text-lg">determines how well therapy works.</p>
            </div>

            <div className="mb-12">
              <p className="text-gray-600 mb-2">Find out which of our psychologists fits you best.</p>
              <p className="text-sm text-gray-500 mb-8">
                Talk to our AI for a few minutes and get a personalized match—<br/>
                we'll help you understand you the fastest.
              </p>
              <p className="text-sm font-medium text-gray-700 mb-8">
                Private. Simple. Accurate.
              </p>
            </div>

            <button
              onClick={startSession}
              className="px-8 py-3 bg-white border-2 border-gray-800 text-gray-800 font-medium rounded hover:bg-gray-800 hover:text-white transition-all"
            >
              START CONVERSATION
            </button>

            <div className="mt-8 text-xs text-gray-400 space-y-1">
              <p>🔒 Our methodology is evidence based</p>
              <p>🔒 We guarantee your privacy</p>
            </div>
          </div>
        )}

        {(sessionState === 'listening' || sessionState === 'ai-speaking' || sessionState === 'processing') && (
          <div className="max-w-xl text-center">
            <CircularVisualizer
              userLevels={userAudioLevels}
              aiLevels={aiAudioLevels}
              isUserActive={sessionState === 'listening'}
              isAiActive={sessionState === 'ai-speaking'}
            />
            
            <p className="text-xl text-gray-700 font-light mb-4">
              {statusMessage}
            </p>
            
            {currentQuestionReason && (
              <div className="max-w-md mx-auto">
                <div className="bg-gray-50 rounded-lg px-4 py-2 border border-gray-200">
                  <p className="text-sm text-gray-600 italic">
                    {currentQuestionReason}
                  </p>
                </div>
              </div>
            )}

            {Object.keys(inferredQAs).length >= 7 && (
              <button
                onClick={generateMatch}
                className="mt-8 px-8 py-3 bg-gray-800 text-white font-medium rounded hover:bg-gray-700 transition-all"
              >
                ✨ Find My Therapist
              </button>
            )}
          </div>
        )}

        {sessionState === 'analyzing' && (
          <div className="text-center">
            <Loader className="animate-spin mx-auto mb-4 text-gray-600" size={48} />
            <p className="text-xl text-gray-700">{statusMessage}</p>
          </div>
        )}

        {sessionState === 'complete' && matchResult && (
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-10 h-10 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <h2 className="text-3xl font-light text-gray-800 mb-2">Perfect Match Found</h2>
              <p className="text-gray-600">We've found the ideal therapist for your needs</p>
            </div>

            <div className="bg-gray-50 rounded-lg p-8 mb-6">
              <h3 className="text-2xl font-medium text-gray-800 mb-3">{matchResult.name}</h3>
              <div className="flex flex-wrap gap-2 mb-4">
                <span className="px-3 py-1 bg-white border border-gray-300 text-gray-700 rounded-full text-sm">
                  {matchResult.specialty}
                </span>
                <span className="px-3 py-1 bg-white border border-gray-300 text-gray-700 rounded-full text-sm">
                  {matchResult.experience}
                </span>
              </div>
              <p className="text-gray-700 leading-relaxed">{matchResult.style}</p>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-8">
              <h4 className="font-medium text-gray-800 mb-2 flex items-center">
                <Heart className="w-4 h-4 mr-2 text-green-600" />
                Why You'll Work Well Together
              </h4>
              <p className="text-gray-700 text-sm leading-relaxed">
                {matchResult.matchReasoning}
              </p>
            </div>

            <div className="flex gap-4 justify-center">
              <button
                onClick={() => window.location.reload()}
                className="px-6 py-2 bg-white border-2 border-gray-800 text-gray-800 font-medium rounded hover:bg-gray-50 transition-all"
              >
                Try Another Match
              </button>
              <button className="px-6 py-2 bg-gray-800 text-white font-medium rounded hover:bg-gray-700 transition-all flex items-center">
                Book First Session <ArrowRight className="ml-2" size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

      <footer className="bg-black text-white p-4 text-center text-sm">
        <p>MINDFULNESS © 2024</p>
      </footer>
    </div>
  );
};

export default MindfulnessMatcher;